import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, FileText, Users, Building2, CheckCircle2, ArrowLeft, Trash2, 
  Edit, BarChart3, Download, Target, Shield, Cog, Truck, Coins,
  ChevronRight, AlertTriangle, Loader2, Link2, Unlink, Sparkles, RefreshCw,
  ChevronDown, ChevronUp, Check, X, ListChecks, Pencil, Copy, ExternalLink, Send
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';

const CATEGORY_ICONS = {
  functional: <Cog className="w-4 h-4" />,
  non_functional: <BarChart3 className="w-4 h-4" />,
  integration: <Link2 className="w-4 h-4" />,
  security: <Shield className="w-4 h-4" />,
  migration: <Truck className="w-4 h-4" />,
  cost: <Coins className="w-4 h-4" />
};

const CATEGORY_COLORS = {
  functional: '#3b82f6',
  non_functional: '#8b5cf6',
  integration: '#06b6d4',
  security: '#ef4444',
  migration: '#f59e0b',
  cost: '#10b981'
};

const CATEGORY_NAMES = {
  functional: 'Funktional',
  non_functional: 'Nicht-funktional',
  integration: 'Integration',
  security: 'Sicherheit',
  migration: 'Migration',
  cost: 'Kosten'
};

const PRIORITY_COLORS = {
  must: 'bg-red-100 text-red-700 border-red-200',
  should: 'bg-amber-100 text-amber-700 border-amber-200',
  could: 'bg-blue-100 text-blue-700 border-blue-200',
  wont: 'bg-gray-100 text-gray-600 border-gray-200'
};

const PRIORITY_NAMES = {
  must: 'Muss',
  should: 'Soll',
  could: 'Kann',
  wont: 'Nicht'
};

const SCORE_LABELS = {
  0: 'Nicht verfügbar',
  1: 'Custom/hoher Aufwand',
  2: 'Erweiterung/Partner',
  3: 'Konfigurierbar',
  4: 'Out-of-the-box',
  5: 'OOB + Best Practice'
};

// Catalog List Component
export const RequirementsCatalogList = () => {
  const navigate = useNavigate();
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCatalog, setNewCatalog] = useState({ name: '', company: '', description: '' });

  useEffect(() => {
    fetchCatalogs();
  }, []);

  const fetchCatalogs = async () => {
    try {
      const response = await axios.get(`${API}/requirements-catalog/catalogs`);
      setCatalogs(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kataloge');
    } finally {
      setLoading(false);
    }
  };

  const createCatalog = async () => {
    if (!newCatalog.name || !newCatalog.company) {
      toast.error('Name und Unternehmen sind erforderlich');
      return;
    }

    try {
      const response = await axios.post(`${API}/requirements-catalog/catalogs`, newCatalog);
      toast.success('Katalog erstellt');
      setShowCreateDialog(false);
      setNewCatalog({ name: '', company: '', description: '' });
      navigate(`/requirements-catalog/${response.data.id}`);
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const deleteCatalog = async (id) => {
    if (!window.confirm('Katalog wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/requirements-catalog/catalogs/${id}`);
      toast.success('Katalog gelöscht');
      fetchCatalogs();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="requirements-catalog-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Anforderungskataloge</h1>
          <p className="text-muted-foreground">Software- und Partnerauswahl systematisch evaluieren</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-catalog-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Katalog
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neuen Anforderungskatalog erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Projektname *</Label>
                <Input
                  value={newCatalog.name}
                  onChange={(e) => setNewCatalog({ ...newCatalog, name: e.target.value })}
                  placeholder="z.B. ERP-Systemauswahl 2025"
                />
              </div>
              <div>
                <Label>Unternehmen *</Label>
                <Input
                  value={newCatalog.company}
                  onChange={(e) => setNewCatalog({ ...newCatalog, company: e.target.value })}
                  placeholder="Ihr Unternehmen"
                />
              </div>
              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={newCatalog.description}
                  onChange={(e) => setNewCatalog({ ...newCatalog, description: e.target.value })}
                  placeholder="Kurze Beschreibung des Evaluationsprojekts"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
              <Button onClick={createCatalog}>Erstellen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {catalogs.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Keine Kataloge vorhanden</h3>
            <p className="text-muted-foreground mb-4">Erstellen Sie Ihren ersten Anforderungskatalog</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Katalog erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {catalogs.map((catalog) => (
            <Card 
              key={catalog.id} 
              className="hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/requirements-catalog/${catalog.id}`)}
              data-testid={`catalog-card-${catalog.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{catalog.name}</CardTitle>
                    <CardDescription>{catalog.company}</CardDescription>
                  </div>
                  <Badge variant={catalog.status === 'completed' ? 'default' : 'secondary'}>
                    {catalog.status === 'completed' ? 'Abgeschlossen' : catalog.status === 'in_progress' ? 'In Bearbeitung' : 'Entwurf'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {catalog.requirement_count || 0} Anforderungen
                  </span>
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    {catalog.vendor_count || 0}/3 Anbieter
                  </span>
                </div>
                <div className="flex justify-end mt-4">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteCatalog(catalog.id);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Catalog Detail Component
export const RequirementsCatalogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [showAddRequirement, setShowAddRequirement] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showEditVendor, setShowEditVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiContext, setAiContext] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [showEditRequirement, setShowEditRequirement] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [expandedRequirements, setExpandedRequirements] = useState({});
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showEditCatalog, setShowEditCatalog] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [newRequirement, setNewRequirement] = useState({
    category: 'functional',
    title: '',
    short_description: '',
    priority: 'should'
  });
  const [newVendor, setNewVendor] = useState({ 
    name: '', 
    contact_person: '', 
    contact_email: '', 
    website: '',
    logo_url: '',
    description: '',
    headquarters: '',
    founded: '',
    employees: ''
  });
  const [vendorLoading, setVendorLoading] = useState(false);
  const [explanationLoading, setExplanationLoading] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchCatalog();
    fetchAnalysis();
  }, [id]);

  const fetchCatalog = async () => {
    try {
      const response = await axios.get(`${API}/requirements-catalog/catalogs/${id}`);
      setCatalog(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
      navigate('/requirements-catalog');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/requirements-catalog/catalogs/${id}/analysis`);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Analysis error:', error);
    }
  };

  const applyTemplates = async () => {
    try {
      const response = await axios.post(`${API}/requirements-catalog/requirements/bulk?catalog_id=${id}&use_templates=true`);
      toast.success(`${response.data.count} Vorlagen hinzugefügt`);
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen der Vorlagen');
    }
  };

  // Open catalog edit dialog
  const openEditCatalog = () => {
    setEditingCatalog({
      name: catalog.name || '',
      company: catalog.company || '',
      description: catalog.description || '',
      goals: catalog.goals || [],
      in_scope: catalog.in_scope || [],
      out_scope: catalog.out_scope || [],
      stakeholders: catalog.stakeholders || [],
      success_criteria: catalog.success_criteria || []
    });
    setShowEditCatalog(true);
  };

  // Update catalog metadata
  const updateCatalog = async () => {
    try {
      await axios.put(`${API}/requirements-catalog/catalogs/${id}`, editingCatalog);
      toast.success('Katalog aktualisiert');
      setShowEditCatalog(false);
      setEditingCatalog(null);
      fetchCatalog();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Helper functions for array editing
  const addGoal = () => {
    setEditingCatalog(prev => ({
      ...prev,
      goals: [...(prev.goals || []), '']
    }));
  };

  const updateGoal = (index, value) => {
    setEditingCatalog(prev => ({
      ...prev,
      goals: prev.goals.map((g, i) => i === index ? value : g)
    }));
  };

  const removeGoal = (index) => {
    setEditingCatalog(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const addScopeItem = (type) => {
    const field = type === 'in' ? 'in_scope' : 'out_scope';
    setEditingCatalog(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), '']
    }));
  };

  const updateScopeItem = (type, index, value) => {
    const field = type === 'in' ? 'in_scope' : 'out_scope';
    setEditingCatalog(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeScopeItem = (type, index) => {
    const field = type === 'in' ? 'in_scope' : 'out_scope';
    setEditingCatalog(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addStakeholder = () => {
    setEditingCatalog(prev => ({
      ...prev,
      stakeholders: [...(prev.stakeholders || []), { name: '', role: '', contact: '' }]
    }));
  };

  const updateStakeholder = (index, field, value) => {
    setEditingCatalog(prev => ({
      ...prev,
      stakeholders: prev.stakeholders.map((sh, i) => i === index ? { ...sh, [field]: value } : sh)
    }));
  };

  const removeStakeholder = (index) => {
    setEditingCatalog(prev => ({
      ...prev,
      stakeholders: prev.stakeholders.filter((_, i) => i !== index)
    }));
  };

  const addSuccessCriterion = () => {
    setEditingCatalog(prev => ({
      ...prev,
      success_criteria: [...(prev.success_criteria || []), { criterion: '', measurement_method: '', target_value: '', deadline: '' }]
    }));
  };

  const updateSuccessCriterion = (index, field, value) => {
    setEditingCatalog(prev => ({
      ...prev,
      success_criteria: prev.success_criteria.map((sc, i) => i === index ? { ...sc, [field]: value } : sc)
    }));
  };

  const removeSuccessCriterion = (index) => {
    setEditingCatalog(prev => ({
      ...prev,
      success_criteria: prev.success_criteria.filter((_, i) => i !== index)
    }));
  };

  // Fetch vendor data from website
  const fetchVendorFromWebsite = async () => {
    if (!newVendor.website) {
      toast.error('Bitte Website-URL eingeben');
      return;
    }

    setVendorLoading(true);
    try {
      const response = await axios.post(`${API}/requirements-catalog/vendors/fetch-from-website?website_url=${encodeURIComponent(newVendor.website)}`);
      const data = response.data;
      setNewVendor(prev => ({
        ...prev,
        name: data.name || prev.name,
        logo_url: data.logo_url || prev.logo_url,
        description: data.description || prev.description,
        headquarters: data.headquarters || prev.headquarters,
        founded: data.founded || prev.founded,
        employees: data.employees || prev.employees,
        website: data.website || prev.website
      }));
      toast.success('Daten erfolgreich geladen');
    } catch (error) {
      toast.error('Fehler beim Laden der Website-Daten');
    } finally {
      setVendorLoading(false);
    }
  };

  const addRequirement = async () => {
    if (!newRequirement.title) {
      toast.error('Titel ist erforderlich');
      return;
    }

    try {
      await axios.post(`${API}/requirements-catalog/requirements`, {
        ...newRequirement,
        catalog_id: id
      });
      toast.success('Anforderung hinzugefügt');
      setShowAddRequirement(false);
      setNewRequirement({ category: 'functional', title: '', short_description: '', priority: 'should' });
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const deleteRequirement = async (reqId) => {
    try {
      await axios.delete(`${API}/requirements-catalog/requirements/${reqId}`);
      toast.success('Anforderung gelöscht');
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const addVendor = async () => {
    if (!newVendor.name) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      await axios.post(`${API}/requirements-catalog/vendors`, {
        ...newVendor,
        catalog_id: id
      });
      toast.success('Anbieter hinzugefügt');
      setShowAddVendor(false);
      setNewVendor({ 
        name: '', 
        contact_person: '', 
        contact_email: '', 
        website: '',
        logo_url: '',
        description: '',
        headquarters: '',
        founded: '',
        employees: ''
      });
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      if (error.response?.data?.detail?.includes('Maximal 3')) {
        toast.error('Maximal 3 Anbieter erlaubt');
      } else {
        toast.error('Fehler beim Hinzufügen');
      }
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      await axios.delete(`${API}/requirements-catalog/vendors/${vendorId}`);
      toast.success('Anbieter gelöscht');
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const generatePortalLink = async (vendorId) => {
    try {
      const response = await axios.post(`${API}/requirements-catalog/vendors/${vendorId}/generate-portal-link`);
      const portalUrl = `${window.location.origin}/vendor-portal/${response.data.token}`;
      await navigator.clipboard.writeText(portalUrl);
      toast.success('Portal-Link in Zwischenablage kopiert!');
      return portalUrl;
    } catch (error) {
      toast.error('Fehler beim Generieren des Links');
      return null;
    }
  };

  const openEditVendor = (vendor) => {
    setEditingVendor({ ...vendor });
    setShowEditVendor(true);
  };

  const updateVendor = async () => {
    if (!editingVendor?.name) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      await axios.put(`${API}/requirements-catalog/vendors/${editingVendor.id}`, {
        name: editingVendor.name,
        contact_person: editingVendor.contact_person,
        contact_email: editingVendor.contact_email,
        website: editingVendor.website
      });
      toast.success('Anbieter aktualisiert');
      setShowEditVendor(false);
      setEditingVendor(null);
      fetchCatalog();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const resetRequirements = async () => {
    if (!window.confirm('Alle bestehenden Anforderungen werden gelöscht und durch die Standard-Vorlagen ersetzt. Fortfahren?')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/requirements-catalog/requirements/reset?catalog_id=${id}`);
      toast.success(`${response.data.created} Anforderungen neu geladen`);
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  // Generate AI explanation for requirement
  const generateExplanation = async (reqId) => {
    setExplanationLoading(prev => ({ ...prev, [reqId]: true }));
    
    try {
      const response = await axios.post(`${API}/requirements-catalog/ai/explain-requirement?requirement_id=${reqId}`);
      // Update the requirement in local state
      setCatalog(prev => ({
        ...prev,
        requirements: prev.requirements.map(r => 
          r.id === reqId ? { ...r, topic_explanation: response.data.explanation } : r
        )
      }));
      toast.success('Erklärung generiert');
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExplanationLoading(prev => ({ ...prev, [reqId]: false }));
    }
  };

  // AI Suggestions
  const generateAISuggestions = async () => {
    if (!aiContext.trim()) {
      toast.error('Bitte geben Sie einen Kontext ein');
      return;
    }

    setAiLoading(true);
    setAiSuggestions([]);
    setSelectedSuggestions([]);

    try {
      const response = await axios.post(`${API}/requirements-catalog/ai/suggest-requirements`, {
        catalog_id: id,
        context: aiContext,
        count: 5
      });
      setAiSuggestions(response.data.suggestions || []);
      // Select all by default
      setSelectedSuggestions(response.data.suggestions?.map((_, i) => i) || []);
    } catch (error) {
      toast.error('Fehler bei KI-Vorschlägen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiLoading(false);
    }
  };

  const toggleSuggestionSelection = (index) => {
    setSelectedSuggestions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const addSelectedSuggestions = async () => {
    if (selectedSuggestions.length === 0) {
      toast.error('Keine Vorschläge ausgewählt');
      return;
    }

    const suggestionsToAdd = selectedSuggestions.map(i => aiSuggestions[i]);

    try {
      await axios.post(`${API}/requirements-catalog/ai/add-suggestions?catalog_id=${id}`, suggestionsToAdd);
      toast.success(`${suggestionsToAdd.length} Anforderungen hinzugefügt`);
      setShowAISuggestions(false);
      setAiSuggestions([]);
      setAiContext('');
      setSelectedSuggestions([]);
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  // Toggle requirement expansion
  const toggleExpanded = (reqId) => {
    setExpandedRequirements(prev => ({
      ...prev,
      [reqId]: !prev[reqId]
    }));
  };

  // Toggle requirement selection
  const toggleRequirementSelection = (reqId) => {
    setSelectedRequirements(prev => 
      prev.includes(reqId) 
        ? prev.filter(id => id !== reqId)
        : [...prev, reqId]
    );
  };

  // Select all visible requirements
  const selectAllVisible = () => {
    const visibleIds = filteredRequirements.map(r => r.id);
    setSelectedRequirements(prev => {
      const allSelected = visibleIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !visibleIds.includes(id));
      } else {
        return [...new Set([...prev, ...visibleIds])];
      }
    });
  };

  // Delete selected requirements
  const deleteSelectedRequirements = async () => {
    if (selectedRequirements.length === 0) return;
    if (!window.confirm(`${selectedRequirements.length} Anforderungen wirklich löschen?`)) return;

    try {
      for (const reqId of selectedRequirements) {
        await axios.delete(`${API}/requirements-catalog/requirements/${reqId}`);
      }
      toast.success(`${selectedRequirements.length} Anforderungen gelöscht`);
      setSelectedRequirements([]);
      setSelectionMode(false);
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Open edit dialog for requirement
  const openEditRequirement = (req) => {
    setEditingRequirement({
      ...req,
      acceptance_criteria: req.acceptance_criteria || [],
      expected_artifacts: req.expected_artifacts || []
    });
    setShowEditRequirement(true);
  };

  // Update requirement
  const updateRequirement = async () => {
    if (!editingRequirement?.title) {
      toast.error('Titel ist erforderlich');
      return;
    }

    try {
      await axios.put(`${API}/requirements-catalog/requirements/${editingRequirement.id}`, {
        category: editingRequirement.category,
        title: editingRequirement.title,
        short_description: editingRequirement.short_description,
        detailed_description: editingRequirement.detailed_description,
        priority: editingRequirement.priority,
        acceptance_criteria: editingRequirement.acceptance_criteria,
        business_value: editingRequirement.business_value,
        notes: editingRequirement.notes
      });
      toast.success('Anforderung aktualisiert');
      setShowEditRequirement(false);
      setEditingRequirement(null);
      fetchCatalog();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const updateEvaluation = async (requirementId, vendorId, score) => {
    try {
      await axios.post(`${API}/requirements-catalog/evaluations`, {
        catalog_id: id,
        requirement_id: requirementId,
        vendor_id: vendorId,
        score: parseInt(score)
      });
      fetchCatalog();
      fetchAnalysis();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const getEvaluationScore = (requirementId, vendorId) => {
    const evaluation = catalog?.evaluations?.find(
      e => e.requirement_id === requirementId && e.vendor_id === vendorId
    );
    return evaluation?.score ?? '';
  };

  const filteredRequirements = catalog?.requirements?.filter(
    r => selectedCategory === 'all' || r.category === selectedCategory
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!catalog) return null;

  // Prepare radar chart data
  const radarData = analysis?.vendor_scores?.length > 0 
    ? Object.keys(CATEGORY_NAMES).map(cat => ({
        category: CATEGORY_NAMES[cat],
        ...Object.fromEntries(
          analysis.vendor_scores.map(v => [v.vendor_name, v.category_scores[cat] || 0])
        )
      }))
    : [];

  return (
    <div className="space-y-6" data-testid="requirements-catalog-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/requirements-catalog')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{catalog.name}</h1>
            <p className="text-muted-foreground">{catalog.company}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Übersicht</TabsTrigger>
          <TabsTrigger value="requirements">Anforderungen ({catalog.requirements?.length || 0})</TabsTrigger>
          <TabsTrigger value="vendors">Anbieter ({catalog.vendors?.length || 0}/3)</TabsTrigger>
          <TabsTrigger value="evaluation">Bewertung</TabsTrigger>
          <TabsTrigger value="analysis">Auswertung</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Project Context Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Zielbild & Scope
              </CardTitle>
              <CardDescription>
                Definieren Sie das Ziel der Softwareevaluation und den Umfang
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Description */}
              <div>
                <Label className="text-xs text-muted-foreground uppercase">Projektbeschreibung</Label>
                <p className="mt-1">{catalog.description || <span className="text-muted-foreground italic">Keine Beschreibung hinterlegt</span>}</p>
              </div>

              {/* Goals / Business Outcomes */}
              <div>
                <Label className="text-xs text-muted-foreground uppercase mb-2 block">Ziele / Business Outcomes</Label>
                {catalog.goals?.length > 0 ? (
                  <ul className="space-y-1">
                    {catalog.goals.map((goal, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
                        <span>{goal}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground italic text-sm">Keine Ziele definiert</p>
                )}
              </div>

              {/* In Scope / Out of Scope */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Label className="text-xs text-green-600 uppercase font-semibold mb-2 block">In Scope</Label>
                  {catalog.in_scope?.length > 0 ? (
                    <ul className="space-y-1 text-sm">
                      {catalog.in_scope.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground italic text-sm">Nicht definiert</p>
                  )}
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <Label className="text-xs text-red-600 uppercase font-semibold mb-2 block">Out of Scope</Label>
                  {catalog.out_scope?.length > 0 ? (
                    <ul className="space-y-1 text-sm">
                      {catalog.out_scope.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <X className="w-4 h-4 mt-0.5 text-red-500 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground italic text-sm">Nicht definiert</p>
                  )}
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={openEditCatalog}>
                <Pencil className="w-4 h-4 mr-2" />
                Zielbild bearbeiten
              </Button>
            </CardContent>
          </Card>

          {/* Stakeholders Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Stakeholder
              </CardTitle>
              <CardDescription>
                Beteiligte Personen und ihre Rollen im Evaluationsprozess
              </CardDescription>
            </CardHeader>
            <CardContent>
              {catalog.stakeholders?.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {catalog.stakeholders.map((sh, i) => (
                    <div key={i} className="p-3 rounded-lg border flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                        {sh.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium">{sh.name}</div>
                        <Badge variant="outline" className="text-xs mt-1">{sh.role}</Badge>
                        {sh.contact && <div className="text-xs text-muted-foreground mt-1">{sh.contact}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic text-sm">Keine Stakeholder definiert</p>
              )}
              <Button variant="outline" size="sm" className="mt-4" onClick={openEditCatalog}>
                <Plus className="w-4 h-4 mr-2" />
                Stakeholder verwalten
              </Button>
            </CardContent>
          </Card>

          {/* Success Criteria / KPIs Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Erfolgskriterien & KPIs
              </CardTitle>
              <CardDescription>
                Messbare Kriterien zur Bewertung des Evaluationserfolgs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {catalog.success_criteria?.length > 0 ? (
                <div className="space-y-3">
                  {catalog.success_criteria.map((sc, i) => (
                    <div key={i} className="p-4 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="font-medium">{sc.criterion}</div>
                        {sc.deadline && <Badge variant="outline">{sc.deadline}</Badge>}
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground grid gap-2 md:grid-cols-2">
                        <div>
                          <span className="font-medium text-foreground">Messmethode:</span> {sc.measurement_method}
                        </div>
                        <div>
                          <span className="font-medium text-foreground">Zielwert:</span> {sc.target_value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic text-sm">Keine Erfolgskriterien definiert</p>
              )}
              <Button variant="outline" size="sm" className="mt-4" onClick={openEditCatalog}>
                <Plus className="w-4 h-4 mr-2" />
                Erfolgskriterien verwalten
              </Button>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Anforderungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{catalog.requirements?.length || 0}</div>
                {selectedRequirements.length > 0 && (
                  <div className="text-xs text-primary mt-1">{selectedRequirements.length} ausgewählt</div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Anbieter</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{catalog.vendors?.length || 0} / 3</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Bewertungen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{catalog.evaluations?.length || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Fortschritt</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analysis?.completion_rate || 0}%</div>
                <Progress value={analysis?.completion_rate || 0} className="mt-2" />
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Schnellstart</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(!catalog.requirements || catalog.requirements.length === 0) && (
                <Button onClick={applyTemplates} className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  Standard-Anforderungen aus Vorlage laden
                </Button>
              )}
              {catalog.requirements && catalog.requirements.length > 0 && (
                <Button variant="outline" onClick={resetRequirements} className="w-full justify-start text-amber-600 hover:text-amber-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Anforderungen auf Standard-Vorlagen zurücksetzen
                </Button>
              )}
              <Button variant="outline" onClick={() => { setActiveTab('requirements'); setShowAddRequirement(true); }} className="w-full justify-start">
                <Plus className="w-4 h-4 mr-2" />
                Eigene Anforderung hinzufügen
              </Button>
              {catalog.vendors?.length < 3 && (
                <Button variant="outline" onClick={() => { setActiveTab('vendors'); setShowAddVendor(true); }} className="w-full justify-start">
                  <Building2 className="w-4 h-4 mr-2" />
                  Anbieter hinzufügen ({3 - (catalog.vendors?.length || 0)} verfügbar)
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Category Summary */}
          {analysis?.category_summary && (
            <Card>
              <CardHeader>
                <CardTitle>Anforderungen nach Kategorie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-3">
                  {Object.entries(analysis.category_summary).map(([cat, data]) => (
                    <div key={cat} className="p-3 rounded-lg border" style={{ borderColor: CATEGORY_COLORS[cat] + '40' }}>
                      <div className="flex items-center gap-2 mb-2">
                        {CATEGORY_ICONS[cat]}
                        <span className="font-medium">{CATEGORY_NAMES[cat]}</span>
                      </div>
                      <div className="text-2xl font-bold">{data.total}</div>
                      <div className="flex gap-2 mt-1 text-xs">
                        <Badge variant="outline" className={PRIORITY_COLORS.must}>{data.must} Muss</Badge>
                        <Badge variant="outline" className={PRIORITY_COLORS.should}>{data.should} Soll</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Requirements Tab */}
        <TabsContent value="requirements" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Kategorie filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                    <SelectItem key={key} value={key}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Selection Mode Toggle */}
              <Button 
                variant={selectionMode ? "default" : "outline"} 
                size="sm"
                onClick={() => { setSelectionMode(!selectionMode); if (selectionMode) setSelectedRequirements([]); }}
              >
                <ListChecks className="w-4 h-4 mr-2" />
                {selectionMode ? 'Auswahl beenden' : 'Auswählen'}
              </Button>
            </div>
            <div className="flex gap-2">
              {/* AI Suggestions Button */}
              <Dialog open={showAISuggestions} onOpenChange={setShowAISuggestions}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="ai-suggestions-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    KI-Vorschläge
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      KI-gestützte Anforderungsvorschläge
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Beschreiben Sie Ihren Anwendungsfall</Label>
                      <Textarea
                        value={aiContext}
                        onChange={(e) => setAiContext(e.target.value)}
                        placeholder="z.B. Wir suchen ein CRM-System für unser Vertriebsteam mit 50 Mitarbeitern. Wichtig sind Anbindungen an unser ERP-System und mobile Nutzung."
                        className="min-h-[100px]"
                      />
                    </div>
                    <Button 
                      onClick={generateAISuggestions} 
                      disabled={aiLoading || !aiContext.trim()}
                      className="w-full"
                    >
                      {aiLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generiere Vorschläge...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Anforderungen generieren
                        </>
                      )}
                    </Button>

                    {aiSuggestions.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Vorgeschlagene Anforderungen</Label>
                          <span className="text-sm text-muted-foreground">
                            {selectedSuggestions.length} von {aiSuggestions.length} ausgewählt
                          </span>
                        </div>
                        {aiSuggestions.map((suggestion, index) => (
                          <Card 
                            key={index} 
                            className={`cursor-pointer transition-all ${selectedSuggestions.includes(index) ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'}`}
                            onClick={() => toggleSuggestionSelection(index)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <Checkbox 
                                  checked={selectedSuggestions.includes(index)}
                                  onCheckedChange={() => toggleSuggestionSelection(index)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span style={{ color: CATEGORY_COLORS[suggestion.category] }}>
                                      {CATEGORY_ICONS[suggestion.category]}
                                    </span>
                                    <span className="font-medium">{suggestion.title}</span>
                                    <Badge variant="outline" className={PRIORITY_COLORS[suggestion.priority]}>
                                      {PRIORITY_NAMES[suggestion.priority]}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{suggestion.short_description}</p>
                                  {suggestion.acceptance_criteria?.length > 0 && (
                                    <div className="mt-2 text-xs text-muted-foreground">
                                      <span className="font-medium">Akzeptanzkriterien:</span>
                                      <ul className="list-disc list-inside ml-2">
                                        {suggestion.acceptance_criteria.slice(0, 2).map((c, i) => (
                                          <li key={i}>{c}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAISuggestions(false)}>Abbrechen</Button>
                    {aiSuggestions.length > 0 && (
                      <Button onClick={addSelectedSuggestions} disabled={selectedSuggestions.length === 0}>
                        <Plus className="w-4 h-4 mr-2" />
                        {selectedSuggestions.length} hinzufügen
                      </Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Manual Add Requirement */}
              <Dialog open={showAddRequirement} onOpenChange={setShowAddRequirement}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Anforderung hinzufügen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Neue Anforderung</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Kategorie</Label>
                      <Select value={newRequirement.category} onValueChange={(v) => setNewRequirement({ ...newRequirement, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                            <SelectItem key={key} value={key}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Titel *</Label>
                      <Input
                        value={newRequirement.title}
                        onChange={(e) => setNewRequirement({ ...newRequirement, title: e.target.value })}
                        placeholder="Anforderungstitel"
                      />
                    </div>
                    <div>
                      <Label>Kurzbeschreibung</Label>
                      <Textarea
                        value={newRequirement.short_description}
                        onChange={(e) => setNewRequirement({ ...newRequirement, short_description: e.target.value })}
                        placeholder="Beschreibung der Anforderung"
                      />
                    </div>
                    <div>
                      <Label>Priorität (MoSCoW)</Label>
                      <Select value={newRequirement.priority} onValueChange={(v) => setNewRequirement({ ...newRequirement, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="must">Muss (Must)</SelectItem>
                          <SelectItem value="should">Soll (Should)</SelectItem>
                          <SelectItem value="could">Kann (Could)</SelectItem>
                          <SelectItem value="wont">Nicht (Won&apos;t)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddRequirement(false)}>Abbrechen</Button>
                    <Button onClick={addRequirement}>Hinzufügen</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {filteredRequirements.length === 0 ? (
            <Card className="text-center py-8">
              <CardContent>
                <p className="text-muted-foreground mb-4">Keine Anforderungen in dieser Kategorie</p>
                <Button variant="outline" onClick={applyTemplates}>
                  Standard-Vorlagen laden
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Selection Mode Toolbar */}
              {selectionMode && (
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button variant="outline" size="sm" onClick={selectAllVisible}>
                        <ListChecks className="w-4 h-4 mr-2" />
                        {filteredRequirements.every(r => selectedRequirements.includes(r.id)) ? 'Alle abwählen' : 'Alle auswählen'}
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {selectedRequirements.length} ausgewählt
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={() => { setSelectionMode(false); setActiveTab('evaluation'); }}
                        disabled={selectedRequirements.length === 0}
                      >
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Zur Bewertung ({selectedRequirements.length})
                      </Button>
                      <Button variant="destructive" size="sm" onClick={deleteSelectedRequirements} disabled={selectedRequirements.length === 0}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setSelectionMode(false); }}>
                        <X className="w-4 h-4 mr-2" />
                        Modus beenden
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {filteredRequirements.map((req) => (
                <Card 
                  key={req.id} 
                  className={`transition-all ${expandedRequirements[req.id] ? 'border-primary/50' : ''} ${selectedRequirements.includes(req.id) ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'}`}
                >
                  <CardContent className="p-0">
                    {/* Main Row - Always Visible */}
                    <div 
                      className="p-4 flex items-center gap-3 cursor-pointer"
                      onClick={() => !selectionMode && toggleExpanded(req.id)}
                    >
                      {/* Selection Checkbox */}
                      {selectionMode && (
                        <Checkbox 
                          checked={selectedRequirements.includes(req.id)}
                          onCheckedChange={() => toggleRequirementSelection(req.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      
                      {/* Expand/Collapse Icon */}
                      {!selectionMode && (
                        <div className="text-muted-foreground">
                          {expandedRequirements[req.id] ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      )}

                      {/* Category Icon */}
                      <div style={{ color: CATEGORY_COLORS[req.category] }}>
                        {CATEGORY_ICONS[req.category]}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-muted-foreground">{req.display_id}</span>
                          <h4 className="font-medium">{req.title}</h4>
                          <Badge variant="outline" className={PRIORITY_COLORS[req.priority]}>
                            {PRIORITY_NAMES[req.priority]}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 truncate">{req.short_description}</p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={(e) => { e.stopPropagation(); openEditRequirement(req); }}
                          data-testid={`edit-req-${req.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={(e) => { e.stopPropagation(); deleteRequirement(req.id); }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedRequirements[req.id] && (
                      <div className="px-4 pb-4 pt-0 border-t bg-muted/30">
                        <div className="grid gap-4 md:grid-cols-2 pt-4">
                          {/* Left Column */}
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs text-muted-foreground uppercase">Beschreibung</Label>
                              <p className="text-sm mt-1">{req.short_description || 'Keine Beschreibung'}</p>
                            </div>

                            {req.detailed_description && (
                              <div>
                                <Label className="text-xs text-muted-foreground uppercase">Details</Label>
                                <p className="text-sm mt-1">{req.detailed_description}</p>
                              </div>
                            )}

                            {req.business_value && (
                              <div>
                                <Label className="text-xs text-muted-foreground uppercase">Business Value</Label>
                                <p className="text-sm mt-1">{req.business_value}</p>
                              </div>
                            )}

                            <div>
                              <Label className="text-xs text-muted-foreground uppercase">Kategorie</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <span style={{ color: CATEGORY_COLORS[req.category] }}>
                                  {CATEGORY_ICONS[req.category]}
                                </span>
                                <span className="text-sm">{CATEGORY_NAMES[req.category]}</span>
                                {req.subcategory && (
                                  <span className="text-xs text-muted-foreground">/ {req.subcategory}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="space-y-3">
                            {req.acceptance_criteria?.length > 0 && (
                              <div>
                                <Label className="text-xs text-muted-foreground uppercase">Akzeptanzkriterien</Label>
                                <ul className="list-disc list-inside text-sm mt-1 space-y-1">
                                  {req.acceptance_criteria.map((criterion, i) => (
                                    <li key={i}>{criterion}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {req.expected_artifacts?.length > 0 && (
                              <div>
                                <Label className="text-xs text-muted-foreground uppercase">Erwartete Nachweise</Label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {req.expected_artifacts.map((artifact, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">{artifact}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {req.notes && (
                              <div>
                                <Label className="text-xs text-muted-foreground uppercase">Notizen</Label>
                                <p className="text-sm mt-1">{req.notes}</p>
                              </div>
                            )}

                            <div className="pt-2 flex gap-2 flex-wrap">
                              <Button variant="outline" size="sm" onClick={() => openEditRequirement(req)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Bearbeiten
                              </Button>
                              {!req.topic_explanation && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => generateExplanation(req.id)}
                                  disabled={explanationLoading[req.id]}
                                >
                                  {explanationLoading[req.id] ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-4 h-4 mr-2" />
                                  )}
                                  Erklärung generieren
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Topic Explanation */}
                        {req.topic_explanation && (
                          <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/20">
                            <div className="flex items-center gap-2 mb-3">
                              <Sparkles className="w-5 h-5 text-primary" />
                              <Label className="text-xs text-primary uppercase font-semibold">KI-Erklärung zum Thema</Label>
                            </div>
                            <div 
                              className="prose prose-sm max-w-none dark:prose-invert"
                              dangerouslySetInnerHTML={{ 
                                __html: req.topic_explanation
                                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                  .replace(/^### (.*$)/gm, '<h4 class="text-sm font-semibold mt-3 mb-1">$1</h4>')
                                  .replace(/^## (.*$)/gm, '<h3 class="text-base font-semibold mt-4 mb-2">$1</h3>')
                                  .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
                                  .replace(/\n/g, '<br/>')
                              }}
                            />
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="mt-3"
                              onClick={() => generateExplanation(req.id)}
                              disabled={explanationLoading[req.id]}
                            >
                              {explanationLoading[req.id] ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Neu generieren
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Edit Requirement Dialog */}
          <Dialog open={showEditRequirement} onOpenChange={setShowEditRequirement}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Anforderung bearbeiten</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Kategorie</Label>
                    <Select 
                      value={editingRequirement?.category || 'functional'} 
                      onValueChange={(v) => setEditingRequirement({ ...editingRequirement, category: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                          <SelectItem key={key} value={key}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priorität (MoSCoW)</Label>
                    <Select 
                      value={editingRequirement?.priority || 'should'} 
                      onValueChange={(v) => setEditingRequirement({ ...editingRequirement, priority: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="must">Muss (Must)</SelectItem>
                        <SelectItem value="should">Soll (Should)</SelectItem>
                        <SelectItem value="could">Kann (Could)</SelectItem>
                        <SelectItem value="wont">Nicht (Won&apos;t)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Titel *</Label>
                  <Input
                    value={editingRequirement?.title || ''}
                    onChange={(e) => setEditingRequirement({ ...editingRequirement, title: e.target.value })}
                    placeholder="Anforderungstitel"
                  />
                </div>

                <div>
                  <Label>Kurzbeschreibung</Label>
                  <Textarea
                    value={editingRequirement?.short_description || ''}
                    onChange={(e) => setEditingRequirement({ ...editingRequirement, short_description: e.target.value })}
                    placeholder="Kurze Beschreibung"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Detaillierte Beschreibung</Label>
                  <Textarea
                    value={editingRequirement?.detailed_description || ''}
                    onChange={(e) => setEditingRequirement({ ...editingRequirement, detailed_description: e.target.value })}
                    placeholder="Ausführliche Beschreibung der Anforderung..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label>Business Value / Nutzen</Label>
                  <Textarea
                    value={editingRequirement?.business_value || ''}
                    onChange={(e) => setEditingRequirement({ ...editingRequirement, business_value: e.target.value })}
                    placeholder="Welchen geschäftlichen Nutzen bringt diese Anforderung?"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Akzeptanzkriterien (je Zeile ein Kriterium)</Label>
                  <Textarea
                    value={editingRequirement?.acceptance_criteria?.join('\n') || ''}
                    onChange={(e) => setEditingRequirement({ 
                      ...editingRequirement, 
                      acceptance_criteria: e.target.value.split('\n').filter(c => c.trim())
                    })}
                    placeholder="Die Funktion muss...&#10;Das System soll..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Notizen</Label>
                  <Textarea
                    value={editingRequirement?.notes || ''}
                    onChange={(e) => setEditingRequirement({ ...editingRequirement, notes: e.target.value })}
                    placeholder="Interne Notizen..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowEditRequirement(false); setEditingRequirement(null); }}>
                  Abbrechen
                </Button>
                <Button onClick={updateRequirement}>
                  Speichern
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showAddVendor} onOpenChange={setShowAddVendor}>
              <DialogTrigger asChild>
                <Button disabled={catalog.vendors?.length >= 3}>
                  <Plus className="w-4 h-4 mr-2" />
                  Anbieter hinzufügen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Neuen Anbieter hinzufügen</DialogTitle>
                  <CardDescription>Geben Sie die Website ein, um automatisch Daten zu laden</CardDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Website with auto-fill */}
                  <div>
                    <Label>Website</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newVendor.website}
                        onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                        placeholder="www.anbieter.com"
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        variant="secondary" 
                        onClick={fetchVendorFromWebsite}
                        disabled={vendorLoading || !newVendor.website}
                      >
                        {vendorLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Daten laden
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Preview with logo */}
                  {(newVendor.logo_url || newVendor.name) && (
                    <Card className="bg-muted/50">
                      <CardContent className="p-4 flex items-start gap-4">
                        {newVendor.logo_url ? (
                          <img 
                            src={newVendor.logo_url} 
                            alt={newVendor.name}
                            className="w-16 h-16 object-contain rounded border bg-white p-1"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-16 h-16 rounded border bg-white flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-semibold">{newVendor.name || 'Kein Name'}</div>
                          {newVendor.description && (
                            <p className="text-sm text-muted-foreground mt-1">{newVendor.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            {newVendor.headquarters && <Badge variant="outline">{newVendor.headquarters}</Badge>}
                            {newVendor.founded && <Badge variant="outline">Seit {newVendor.founded}</Badge>}
                            {newVendor.employees && <Badge variant="outline">{newVendor.employees} MA</Badge>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={newVendor.name}
                        onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                        placeholder="Anbietername"
                      />
                    </div>
                    <div>
                      <Label>Beschreibung</Label>
                      <Input
                        value={newVendor.description}
                        onChange={(e) => setNewVendor({ ...newVendor, description: e.target.value })}
                        placeholder="Kurze Beschreibung"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>Hauptsitz</Label>
                      <Input
                        value={newVendor.headquarters}
                        onChange={(e) => setNewVendor({ ...newVendor, headquarters: e.target.value })}
                        placeholder="Stadt, Land"
                      />
                    </div>
                    <div>
                      <Label>Gründungsjahr</Label>
                      <Input
                        value={newVendor.founded}
                        onChange={(e) => setNewVendor({ ...newVendor, founded: e.target.value })}
                        placeholder="z.B. 2010"
                      />
                    </div>
                    <div>
                      <Label>Mitarbeiter</Label>
                      <Input
                        value={newVendor.employees}
                        onChange={(e) => setNewVendor({ ...newVendor, employees: e.target.value })}
                        placeholder="z.B. 500"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Ansprechpartner</Label>
                      <Input
                        value={newVendor.contact_person}
                        onChange={(e) => setNewVendor({ ...newVendor, contact_person: e.target.value })}
                        placeholder="Name des Ansprechpartners"
                      />
                    </div>
                    <div>
                      <Label>E-Mail</Label>
                      <Input
                        value={newVendor.contact_email}
                        onChange={(e) => setNewVendor({ ...newVendor, contact_email: e.target.value })}
                        placeholder="email@anbieter.com"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddVendor(false)}>Abbrechen</Button>
                  <Button onClick={addVendor} disabled={!newVendor.name}>Hinzufügen</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Vendor Dialog */}
            <Dialog open={showEditVendor} onOpenChange={setShowEditVendor}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Anbieter bearbeiten</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={editingVendor?.name || ''}
                      onChange={(e) => setEditingVendor({ ...editingVendor, name: e.target.value })}
                      placeholder="Anbietername"
                    />
                  </div>
                  <div>
                    <Label>Ansprechpartner</Label>
                    <Input
                      value={editingVendor?.contact_person || ''}
                      onChange={(e) => setEditingVendor({ ...editingVendor, contact_person: e.target.value })}
                      placeholder="Name des Ansprechpartners"
                    />
                  </div>
                  <div>
                    <Label>E-Mail</Label>
                    <Input
                      value={editingVendor?.contact_email || ''}
                      onChange={(e) => setEditingVendor({ ...editingVendor, contact_email: e.target.value })}
                      placeholder="email@anbieter.com"
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={editingVendor?.website || ''}
                      onChange={(e) => setEditingVendor({ ...editingVendor, website: e.target.value })}
                      placeholder="https://www.anbieter.com"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowEditVendor(false); setEditingVendor(null); }}>Abbrechen</Button>
                  <Button onClick={updateVendor}>Speichern</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {catalog.vendors?.map((vendor) => (
              <Card key={vendor.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {/* Logo */}
                      {vendor.logo_url ? (
                        <img 
                          src={vendor.logo_url} 
                          alt={vendor.name}
                          className="w-12 h-12 object-contain rounded border bg-white p-1"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded border bg-muted flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg">{vendor.name}</CardTitle>
                        {vendor.description && (
                          <CardDescription className="line-clamp-2 mt-1">{vendor.description}</CardDescription>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditVendor(vendor)} data-testid={`edit-vendor-${vendor.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteVendor(vendor.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {/* Company Info */}
                  {(vendor.headquarters || vendor.founded || vendor.employees) && (
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {vendor.headquarters && <Badge variant="outline">{vendor.headquarters}</Badge>}
                      {vendor.founded && <Badge variant="outline">Seit {vendor.founded}</Badge>}
                      {vendor.employees && <Badge variant="outline">{vendor.employees} MA</Badge>}
                    </div>
                  )}
                  {vendor.contact_person && (
                    <div className="text-muted-foreground">
                      <span className="font-medium">Kontakt:</span> {vendor.contact_person}
                    </div>
                  )}
                  {vendor.contact_email && (
                    <div className="text-muted-foreground">{vendor.contact_email}</div>
                  )}
                  {vendor.website && (
                    <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      Website
                    </a>
                  )}
                  
                  {/* Portal Link */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-muted-foreground">
                        {vendor.assessment_submitted ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Selbstauskunft eingereicht
                          </span>
                        ) : vendor.portal_token ? (
                          <span className="flex items-center gap-1 text-amber-600">
                            <Send className="w-3 h-3" />
                            Link generiert
                          </span>
                        ) : (
                          <span>Selbstauskunft anfordern</span>
                        )}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => generatePortalLink(vendor.id)}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Link kopieren
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {(catalog.vendors?.length || 0) < 3 && (
              <Card className="border-dashed flex items-center justify-center min-h-[150px] cursor-pointer hover:bg-muted/50" onClick={() => setShowAddVendor(true)}>
                <div className="text-center text-muted-foreground">
                  <Plus className="w-8 h-8 mx-auto mb-2" />
                  <p>Anbieter hinzufügen</p>
                </div>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Evaluation Tab */}
        <TabsContent value="evaluation" className="space-y-4">
          {/* Selection Info */}
          {selectedRequirements.length > 0 && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="font-medium">{selectedRequirements.length} Anforderungen ausgewählt</span>
                  <span className="text-muted-foreground">- nur diese werden in der Bewertung angezeigt</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setSelectedRequirements([])}>
                  Alle anzeigen
                </Button>
              </CardContent>
            </Card>
          )}

          {(!catalog.vendors || catalog.vendors.length === 0) ? (
            <Card className="text-center py-8">
              <CardContent>
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Fügen Sie zuerst Anbieter hinzu, um die Bewertung zu starten.</p>
              </CardContent>
            </Card>
          ) : (!catalog.requirements || catalog.requirements.length === 0) ? (
            <Card className="text-center py-8">
              <CardContent>
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Fügen Sie zuerst Anforderungen hinzu.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">ID</TableHead>
                        <TableHead>Anforderung</TableHead>
                        <TableHead className="w-20">Prio</TableHead>
                        {catalog.vendors.map((vendor) => (
                          <TableHead key={vendor.id} className="w-32 text-center">{vendor.name}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedRequirements.length > 0 
                        ? catalog.requirements.filter(r => selectedRequirements.includes(r.id))
                        : catalog.requirements
                      ).map((req) => (
                        <TableRow key={req.id}>
                          <TableCell className="font-mono text-xs">{req.display_id}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span style={{ color: CATEGORY_COLORS[req.category] }}>
                                {CATEGORY_ICONS[req.category]}
                              </span>
                              <span className="font-medium">{req.title}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={PRIORITY_COLORS[req.priority]}>
                              {PRIORITY_NAMES[req.priority]}
                            </Badge>
                          </TableCell>
                          {catalog.vendors.map((vendor) => (
                            <TableCell key={vendor.id} className="text-center">
                              <Select
                                value={getEvaluationScore(req.id, vendor.id)?.toString() || ''}
                                onValueChange={(v) => updateEvaluation(req.id, vendor.id, v)}
                              >
                                <SelectTrigger className="w-20 mx-auto">
                                  <SelectValue placeholder="-" />
                                </SelectTrigger>
                                <SelectContent>
                                  {[0, 1, 2, 3, 4, 5].map((score) => (
                                    <SelectItem key={score} value={score.toString()}>
                                      {score}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Score Legend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Bewertungsskala (0-5)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(SCORE_LABELS).map(([score, label]) => (
                  <Badge key={score} variant="outline" className="text-xs">
                    <span className="font-bold mr-1">{score}:</span> {label}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-6">
          {analysis && analysis.vendor_scores?.length > 0 ? (
            <>
              {/* Overall Ranking */}
              <Card>
                <CardHeader>
                  <CardTitle>Gesamtranking</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analysis.vendor_scores.map((vendor, index) => (
                      <div key={vendor.vendor_id} className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{vendor.vendor_name}</span>
                            <span className="font-bold text-lg">{vendor.overall_score}%</span>
                          </div>
                          <Progress value={vendor.overall_score} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Radar Chart */}
              {radarData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Vergleich nach Kategorien</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[400px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="category" />
                          <PolarRadiusAxis domain={[0, 100]} />
                          {analysis.vendor_scores.map((vendor, idx) => (
                            <Radar
                              key={vendor.vendor_id}
                              name={vendor.vendor_name}
                              dataKey={vendor.vendor_name}
                              stroke={['#3b82f6', '#ef4444', '#10b981'][idx]}
                              fill={['#3b82f6', '#ef4444', '#10b981'][idx]}
                              fillOpacity={0.2}
                            />
                          ))}
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Category Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Detailbewertung nach Kategorie</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kategorie</TableHead>
                        <TableHead className="text-center">Gewichtung</TableHead>
                        {analysis.vendor_scores.map((vendor) => (
                          <TableHead key={vendor.vendor_id} className="text-center">{vendor.vendor_name}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(CATEGORY_NAMES).map(([cat, name]) => (
                        <TableRow key={cat}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <span style={{ color: CATEGORY_COLORS[cat] }}>{CATEGORY_ICONS[cat]}</span>
                              {name}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {analysis.category_weights[cat] || 0}%
                          </TableCell>
                          {analysis.vendor_scores.map((vendor) => (
                            <TableCell key={vendor.vendor_id} className="text-center">
                              <span className={vendor.category_scores[cat] >= 70 ? 'text-green-600 font-bold' : vendor.category_scores[cat] >= 40 ? 'text-amber-600' : 'text-red-600'}>
                                {vendor.category_scores[cat] || 0}%
                              </span>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Noch keine Auswertung verfügbar</h3>
                <p className="text-muted-foreground">Fügen Sie Anforderungen, Anbieter und Bewertungen hinzu.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Catalog Dialog */}
      <Dialog open={showEditCatalog} onOpenChange={setShowEditCatalog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Katalog bearbeiten</DialogTitle>
            <CardDescription>Zielbild, Scope, Stakeholder und Erfolgskriterien definieren</CardDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Name *</Label>
                <Input
                  value={editingCatalog?.name || ''}
                  onChange={(e) => setEditingCatalog({ ...editingCatalog, name: e.target.value })}
                  placeholder="Katalogname"
                />
              </div>
              <div>
                <Label>Unternehmen</Label>
                <Input
                  value={editingCatalog?.company || ''}
                  onChange={(e) => setEditingCatalog({ ...editingCatalog, company: e.target.value })}
                  placeholder="Firma AG"
                />
              </div>
            </div>

            <div>
              <Label>Projektbeschreibung</Label>
              <Textarea
                value={editingCatalog?.description || ''}
                onChange={(e) => setEditingCatalog({ ...editingCatalog, description: e.target.value })}
                placeholder="Beschreiben Sie das Projekt und den Hintergrund der Softwareevaluation..."
                rows={3}
              />
            </div>

            {/* Goals */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ziele / Business Outcomes</Label>
                <Button variant="outline" size="sm" onClick={addGoal}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ziel hinzufügen
                </Button>
              </div>
              <div className="space-y-2">
                {editingCatalog?.goals?.map((goal, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={goal}
                      onChange={(e) => updateGoal(i, e.target.value)}
                      placeholder="z.B. Effizienzsteigerung um 20%"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeGoal(i)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {(!editingCatalog?.goals || editingCatalog.goals.length === 0) && (
                  <p className="text-muted-foreground text-sm italic">Keine Ziele definiert</p>
                )}
              </div>
            </div>

            {/* In Scope / Out of Scope */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-green-600">In Scope</Label>
                  <Button variant="outline" size="sm" onClick={() => addScopeItem('in')}>
                    <Plus className="w-4 h-4 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingCatalog?.in_scope?.map((item, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={item}
                        onChange={(e) => updateScopeItem('in', i, e.target.value)}
                        placeholder="z.B. Finanzbuchhaltung"
                      />
                      <Button variant="ghost" size="icon" onClick={() => removeScopeItem('in', i)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-red-600">Out of Scope</Label>
                  <Button variant="outline" size="sm" onClick={() => addScopeItem('out')}>
                    <Plus className="w-4 h-4 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingCatalog?.out_scope?.map((item, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={item}
                        onChange={(e) => updateScopeItem('out', i, e.target.value)}
                        placeholder="z.B. HR-Modul"
                      />
                      <Button variant="ghost" size="icon" onClick={() => removeScopeItem('out', i)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stakeholders */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Stakeholder</Label>
                <Button variant="outline" size="sm" onClick={addStakeholder}>
                  <Plus className="w-4 h-4 mr-1" />
                  Stakeholder hinzufügen
                </Button>
              </div>
              <div className="space-y-3">
                {editingCatalog?.stakeholders?.map((sh, i) => (
                  <div key={i} className="grid gap-2 md:grid-cols-4 p-3 border rounded-lg">
                    <Input
                      value={sh.name}
                      onChange={(e) => updateStakeholder(i, 'name', e.target.value)}
                      placeholder="Name"
                    />
                    <Select value={sh.role} onValueChange={(v) => updateStakeholder(i, 'role', v)}>
                      <SelectTrigger><SelectValue placeholder="Rolle" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sponsor">Sponsor</SelectItem>
                        <SelectItem value="Projektleiter">Projektleiter</SelectItem>
                        <SelectItem value="Fachbereich">Fachbereich</SelectItem>
                        <SelectItem value="IT">IT</SelectItem>
                        <SelectItem value="Einkauf">Einkauf</SelectItem>
                        <SelectItem value="Legal">Legal / Recht</SelectItem>
                        <SelectItem value="Finanzen">Finanzen</SelectItem>
                        <SelectItem value="Endanwender">Endanwender</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={sh.contact || ''}
                      onChange={(e) => updateStakeholder(i, 'contact', e.target.value)}
                      placeholder="E-Mail / Telefon"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeStakeholder(i)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {(!editingCatalog?.stakeholders || editingCatalog.stakeholders.length === 0) && (
                  <p className="text-muted-foreground text-sm italic">Keine Stakeholder definiert</p>
                )}
              </div>
            </div>

            {/* Success Criteria */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Erfolgskriterien & KPIs</Label>
                <Button variant="outline" size="sm" onClick={addSuccessCriterion}>
                  <Plus className="w-4 h-4 mr-1" />
                  Kriterium hinzufügen
                </Button>
              </div>
              <div className="space-y-3">
                {editingCatalog?.success_criteria?.map((sc, i) => (
                  <div key={i} className="p-3 border rounded-lg space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={sc.criterion}
                        onChange={(e) => updateSuccessCriterion(i, 'criterion', e.target.value)}
                        placeholder="Kriterium (z.B. Einführung innerhalb von 6 Monaten)"
                        className="flex-1"
                      />
                      <Button variant="ghost" size="icon" onClick={() => removeSuccessCriterion(i)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                      <Input
                        value={sc.measurement_method || ''}
                        onChange={(e) => updateSuccessCriterion(i, 'measurement_method', e.target.value)}
                        placeholder="Messmethode"
                      />
                      <Input
                        value={sc.target_value || ''}
                        onChange={(e) => updateSuccessCriterion(i, 'target_value', e.target.value)}
                        placeholder="Zielwert"
                      />
                      <Input
                        type="date"
                        value={sc.deadline || ''}
                        onChange={(e) => updateSuccessCriterion(i, 'deadline', e.target.value)}
                        placeholder="Deadline"
                      />
                    </div>
                  </div>
                ))}
                {(!editingCatalog?.success_criteria || editingCatalog.success_criteria.length === 0) && (
                  <p className="text-muted-foreground text-sm italic">Keine Erfolgskriterien definiert</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditCatalog(false); setEditingCatalog(null); }}>
              Abbrechen
            </Button>
            <Button onClick={updateCatalog}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequirementsCatalogList;
